import React, { useMemo } from "react";
import { Controller } from "react-hook-form";
import { Col, FormGroup, Label } from "reactstrap";
import { useTranslation } from "react-i18next";
import { HugeiconsIcon } from "@hugeicons/react";
import { Search01Icon } from "@hugeicons/core-free-icons";
import { AntInput } from "@/CommonElements/AntInput";
import { AntSelect } from "@/CommonElements/AntSelect";
import BtnAsLink from "@/Component/BtnAsLink";
import { Row } from "react-bootstrap";
import { Divider, Space } from "antd";
import { Controllers } from "@/CommonElements/Controller";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";
import { SelectCountry } from "@/Component/Form/SelectCountry/SelectCountry";
import { Info } from "@/Component/Info/Info";
import CaseDetails from "./CaseDetails";
import { useBasicDataQuery } from "@/services/basicData";
import { useSystem } from "@/utils/hooks/useLocation";
import { SYSTEMS } from "@/utils/constants";

function PilgrimReportTab({
  form,
  control,
  FormField,
  watch,
  setValue,
  onSearch,
  searchPilgrim,
  canSearchPilgrims = true,
  threeCol,
  fullWidthCol,
  healthCertificateInputRef,
  activeTabKey = "pilgrim",
  existingDocuments = [],
  reportId = null,
  isEditMode = false,
}) {
  const { t } = useTranslation();
  const { currentSystem } = useSystem();
  const { data: basicDataResponse } = useBasicDataQuery({ enabled: true });
  const basicData = basicDataResponse?.data?.data ?? {};
  const serviceCenters = useMemo(
    () => basicData?.service_centers ?? [],
    [basicData?.service_centers]
  );

  const searchKeyOptions = useMemo(
    () => [
      { value: "border_number", label: t("BORDER_NUMBER") },
      { value: "passport_number", label: t("PASSPORT_NUMBER") },
      { value: "nusuk_card_id", label: t("NUSUK_CARD_ID") },
      { value: "visa_number", label: t("VISA_NUMBER") },
    ],
    [t]
  );

  const displayOnlyControllers = useMemo(() => {
    const displayOnlyColProps = { xs: 12, md: 6, lg: 4 };
    return [
      {
        name: "pilgrim_name",
        controllerProps: { name: "pilgrim_name" },
        labelProps: { children: t("PILGRIM_NAME"), className: "fw-bold" },
        layout: { colProps: displayOnlyColProps },
        renderValue: (v) => v || "-",
      },
      {
        name: "nationality",
        controllerProps: { name: "nationality" },
        labelProps: { children: t("NATIONALITY"), className: "fw-bold" },
        layout: { colProps: displayOnlyColProps },
        renderValue: (v) =>
          v == null ? "-" : <SelectCountry.Display value={v} />,
      },
      {
        name: "gender",
        controllerProps: { name: "gender" },
        labelProps: { children: t("GENDER"), className: "fw-bold" },
        layout: { colProps: displayOnlyColProps },
        renderValue: (v) =>
          v === "male" ? t("MALE") : v === "female" ? t("FEMALE") : "-",
      },
      {
        name: "housing_contract",
        controllerProps: { name: "housing_contract" },
        labelProps: { children: t("HOUSING_CONTRACT"), className: "fw-bold" },
        layout: { colProps: displayOnlyColProps },
        renderValue: (v) => {
          if (v == null || v === "") return "-";
          return (
            <Space>
              <span>{v}</span>
              <SelectCachedOptions.Display
                optionKey="makkahContracts"
                campareBy="id"
                value={v}
                render={(option) =>
                  option ? (
                    <Info
                      title={`${t("GROUPS_FILTER_COMPANY")}: ${option.company_name_ar ?? option.house_commercial_name_ar ?? "-"}`}
                    />
                  ) : (
                    <></>
                  )
                }
              />
            </Space>
          );
        },
      },
      {
        name: "service_center",
        controllerProps: { name: "service_center" },
        labelProps: { children: t("SERVICE_CENTER"), className: "fw-bold" },
        layout: { colProps: displayOnlyColProps },
        renderValue: (v) => {
          if (v == null) return "-";
          const option = serviceCenters.find((sc) => String(sc?.id) === String(v));
          return option ? option?.name || "-" : "-";
        },
      },
      {
        name: "campaign_number",
        controllerProps: { name: "campaign_number" },
        labelProps: { children: t("CAMPAIGN_NUMBER"), className: "fw-bold" },
        layout: { colProps: displayOnlyColProps },
        renderValue: (v) => v || "-",
      },
      ...(currentSystem === SYSTEMS.madina && isEditMode
        ? [
            {
              name: "bag_number",
              controllerProps: { name: "bag_number" },
              labelProps: { children: t("BAG_NUMBER"), className: "fw-bold" },
              layout: { colProps: displayOnlyColProps },
              renderValue: (v) => v || "-",
            },
          ]
        : []),
    ];
  }, [t, serviceCenters, currentSystem, reportId]);

  return (
    <>
      <Row
        className="rounded py-3 px-1 mb-4"
        style={{ border: "1px dashed #7a4819" }}
      >
        <Col xs={12} md={6} lg={4}>
          <FormGroup className="mb-3">
            <Label className="fw-bold" for="search_key_type">
              {t("PILGRIM_ID_TYPE")}
            </Label>
            <Controller
              name="search_key_type"
              control={control}
              render={({ field, fieldState: { invalid, error } }) => (
                <>
                  <AntSelect
                    {...field}
                    status={invalid ? "error" : undefined}
                    placeholder={t("SEARCH_KEY_TYPE")}
                    options={searchKeyOptions}
                  />
                  {error && (
                    <div className="text-danger small mt-1">{error.message}</div>
                  )}
                </>
              )}
            />
          </FormGroup>
        </Col>
        <Col xs={12} md={6} lg={4}>
          <FormGroup className="mb-3">
            <Label className="fw-bold" for="pilgrim_id_or_passport">
              {t("PILGRIM_ID_OR_PASSPORT")} *
            </Label>
            <Controller
              name="pilgrim_id_or_passport"
              control={control}
              rules={{
                validate: (value) => {
                  if (activeTabKey === "other") return true;
                  return (value && String(value).trim()) ? true : t("FIELD_REQUIRED");
                },
              }}
              render={({ field, fieldState: { invalid, error } }) => (
                <>
                  <AntInput
                    {...field}
                    status={invalid ? "error" : undefined}
                    id="pilgrim_id_or_passport"
                    placeholder={t("ENTER_PLACEHOLDER")}
                  />
                  {error && (
                    <div className="text-danger small mt-1">{error.message}</div>
                  )}
                </>
              )}
            />
          </FormGroup>
        </Col>
        <Col xs={12} md={6} lg={4} style={{ display: "flex", alignItems: "flex-end" }}>
          <BtnAsLink
            type="button"
            className="btn-secondary btn-sm d-flex align-items-center mb-3 bg-[#E5E4E2] !text-primary"
            icon={<HugeiconsIcon icon={Search01Icon} />}
            onClick={onSearch}
            disabled={searchPilgrim.isPending || !canSearchPilgrims}
          >
            {t("SEARCH")}
          </BtnAsLink>
        </Col>
      </Row>
      <Row>
        <Controllers
          isViewMode={true}
          watch={form.watch}
          controllers={displayOnlyControllers}
        />
        <Col xs={12}>
          <Divider />
        </Col>
        <CaseDetails
          control={control}
          FormField={FormField}
          watch={watch}
          setValue={setValue}
          threeCol={threeCol}
          fullWidthCol={fullWidthCol}
          healthCertificateInputRef={healthCertificateInputRef}
          existingDocuments={existingDocuments}
          reportId={reportId}
        />
      </Row>
    </>
  );
}

export default PilgrimReportTab;
